import axios from 'axios'
import { logger } from './logger'
import { trackApiError } from './analytics'

// Use VITE_BACKEND_URL for Vite (instead of REACT_APP_BACKEND_URL)
const API_BASE_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000'

const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 600000, // 10 minutes default
  maxContentLength: 100 * 1024 * 1024, // 100MB - maximum size of the response
  maxBodyLength: 100 * 1024 * 1024, // 100MB - maximum size of the request body
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('dizeeToken')
    if (token) {
      try {
        const parsedToken = JSON.parse(token)
        config.headers.Authorization = `Bearer ${parsedToken}`
      } catch (e) {
        // If token is not JSON, use as-is
        config.headers.Authorization = `Bearer ${token}`
      }
    }
    
    // Log the actual timeout being used
    console.log('🔧 Axios request interceptor - Final config:', {
      url: config.url,
      method: config.method,
      timeout: config.timeout,
      baseURL: config.baseURL
    })
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for global error logging
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status
    const endpoint = error?.config?.url || 'unknown'
    const msg = error?.response?.data?.message || error.message
    logger.logApiError(endpoint, status || 0, msg)
    if (status && status >= 500) trackApiError(endpoint, status, msg)
    return Promise.reject(error)
  }
)

const ACTIVE_ARTIST_IDS_KEY = 'dizee:activeArtistIds'
const SELECTED_ARTIST_KEY = 'selectedArtist'

/**
 * Migrate legacy selectedArtist to activeArtistIds on first read.
 * Call once at app init or when reading active ids.
 */
function migrateSelectedArtistToActiveIds(): void {
  const raw = localStorage.getItem(SELECTED_ARTIST_KEY)
  if (!raw) return
  try {
    const artist = JSON.parse(raw)
    const id = artist._id || artist.id
    if (id) {
      const existing = getActiveArtistIdsRaw()
      if (!existing.length) {
        localStorage.setItem(ACTIVE_ARTIST_IDS_KEY, JSON.stringify([id]))
      }
      localStorage.removeItem(SELECTED_ARTIST_KEY)
    }
  } catch {
    localStorage.removeItem(SELECTED_ARTIST_KEY)
  }
}

/**
 * Read active artist IDs from storage (no migration).
 * Returns array of strings (user IDs or pending artist IDs).
 */
function getActiveArtistIdsRaw(): string[] {
  try {
    const raw = localStorage.getItem(ACTIVE_ARTIST_IDS_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((id: unknown) => typeof id === 'string') : []
  } catch {
    return []
  }
}

/**
 * Get active artist IDs. Migrates from selectedArtist if needed.
 * For artists (non–team members), returns [current user id] when no active ids set.
 */
export function getActiveArtistIds(): string[] {
  migrateSelectedArtistToActiveIds()
  const ids = getActiveArtistIdsRaw()
  if (ids.length > 0) return ids
  // Artist (no team context): use current user as single "active" id
  try {
    const user = localStorage.getItem('dizeeUser')
    if (user) {
      const u = JSON.parse(user)
      if (u._id && !['manager', 'support', 'promoter'].includes(u.professionRole || '')) {
        return [u._id]
      }
    }
  } catch {
    // ignore
  }
  return []
}

/**
 * Primary artist ID (first in active list). Used for writes when no explicit artist chosen.
 */
export function getPrimaryArtistId(): string | null {
  const ids = getActiveArtistIds()
  return ids.length > 0 ? ids[0] : null
}

/**
 * Set active artist IDs. Persists to localStorage.
 */
export function setActiveArtistIds(ids: string[]): void {
  const valid = Array.isArray(ids) ? ids.filter((id) => typeof id === 'string') : []
  localStorage.setItem(ACTIVE_ARTIST_IDS_KEY, JSON.stringify(valid))
}

const ARTIST_CACHE_KEY = 'dizee:artistCache'

/** Cache artist id -> { _id, name } for display (e.g. Settings, Team Management). Call from Team Hub / ArtistSwitcher when managed artists load. */
export function setArtistCache(artists: { _id?: string; id?: string; name?: string; username?: string }[]): void {
  try {
    const cache: Record<string, { _id: string; name: string; username?: string }> = {}
    artists.forEach((a) => {
      const id = a._id || a.id
      if (id)
        cache[id] = {
          _id: id,
          name: a.name || 'Artist',
          ...(typeof a.username === 'string' && a.username ? { username: a.username } : {}),
        }
    })
    localStorage.setItem(ARTIST_CACHE_KEY, JSON.stringify(cache))
  } catch {
    // ignore
  }
}

/** Primary artist for display (name). Returns null if no primary or not in cache. */
export function getPrimaryArtist(): { _id: string; name: string; username?: string } | null {
  const id = getPrimaryArtistId()
  if (!id) return null
  return getArtistFromCache(id)
}

/** Get artist name (and optional inbox username) from cache by id. */
export function getArtistFromCache(artistId: string): { _id: string; name: string; username?: string } | null {
  try {
    const raw = localStorage.getItem(ARTIST_CACHE_KEY)
    if (!raw) return null
    const cache = JSON.parse(raw) as Record<string, { _id: string; name: string; username?: string }>
    return cache[artistId] || null
  } catch {
    return null
  }
}

/**
 * Toggle one artist ID on/off. Returns new array of active ids.
 */
export function toggleActiveArtistId(artistId: string): string[] {
  migrateSelectedArtistToActiveIds()
  const current = getActiveArtistIdsRaw()
  const has = current.includes(artistId)
  const next = has ? current.filter((id) => id !== artistId) : [...current, artistId]
  setActiveArtistIds(next)
  return next
}

/**
 * Check if an artist ID is currently active.
 */
export function isActiveArtistId(artistId: string): boolean {
  return getActiveArtistIds().includes(artistId)
}

// Helper function to check if user is in team member context (has at least one active artist)
const isTeamMemberContext = (): boolean => {
  return getActiveArtistIds().length > 0
}

// Helper function to get the effective user ID (primary artist for API calls).
// Backend may later support pendingArtistId in body; for now we send userId.
const getEffectiveUserId = (): string | null => {
  return getPrimaryArtistId()
}

// Endpoints that are "current user" scoped (e.g. "my teams", "my invitations").
// They must NOT receive the selected artist's userId, or the backend may return
// the wrong or empty list (e.g. artists that the selected artist manages instead
// of artists this team member can access).
const USER_SCOPED_ENDPOINTS = [
  'team/managed-artists',
  'team/invitation-requests',
  'team/members',
  'team/pending-artists',
  'team/invite-artist-to-dizee',
  'team/claim-artist-invite',
  'promoter/shows',
  'promoter/invite-artist',
  'user/onboarding-completed',
  /** Always the authenticated account — not the "acting as" artist (fixes Settings / profile sync for team members). */
  'user/get-user-article',
  'auth/checkUserName',
]

const shouldAttachUserId = (endpoint: string): boolean => {
  return !USER_SCOPED_ENDPOINTS.includes(endpoint)
}

/** Dispatched on `window` after `dizeeUser` is saved so Sidebar and other UI sync in the same tab. */
export const DIZEE_USER_STORAGE_EVENT = 'dizee:user-storage-updated'

/** User-facing message from an axios/API error (validation text, 400 bodies, etc.). */
export function getApiErrorMessage(err: unknown): string | null {
  const e = err as { response?: { data?: { message?: string; error?: string } | string }; message?: string }
  const d = e?.response?.data
  if (d && typeof d === 'object' && typeof (d as { message?: string }).message === 'string') {
    return (d as { message: string }).message
  }
  if (d && typeof d === 'object' && typeof (d as { error?: string }).error === 'string') {
    return (d as { error: string }).error
  }
  if (typeof d === 'string') return d
  if (e?.message) return e.message
  return null
}

/** Persist the logged-in user object and notify listeners (same-tab `localStorage` writes do not fire `storage`). */
export function persistDizeeUser(next: Record<string, unknown> | null): void {
  if (!next || typeof next !== 'object') return
  try {
    localStorage.setItem('dizeeUser', JSON.stringify(next))
    window.dispatchEvent(new CustomEvent(DIZEE_USER_STORAGE_EVENT))
  } catch {
    /* quota / private mode */
  }
  // Register native push token after auth (lazy-loaded, no-ops on web)
  import('./pushNative').then(m => m.registerNativePush()).catch(() => {})
}

/** Optional: pass params to override or add query params (e.g. { params: { userId: artistId } } for delegated access). */
export const getDataAPI = async (
  endpoint: string,
  token?: string,
  options?: { params?: Record<string, string> }
) => {
  try {
    const config: any = {}
    if (token) {
      config.headers = { Authorization: `Bearer ${token}` }
    }

    config.params = { ...(options?.params || {}) }
    const effectiveUserId = getEffectiveUserId()
    if (!options?.params?.userId && effectiveUserId && shouldAttachUserId(endpoint)) {
      config.params.userId = effectiveUserId
    }

    const response = await api.get(endpoint, config)
    return response.data
  } catch (error) {
    console.error('GET API Error:', error)
    throw error
  }
}

export const postDataAPI = async (endpoint: string, data: any, token?: string, config?: any) => {
  try {
    const requestConfig: any = {
      maxContentLength: 100 * 1024 * 1024, // 100MB
      maxBodyLength: 100 * 1024 * 1024, // 100MB
      ...config // Spread options like timeout
    }
    
    if (token) {
      requestConfig.headers = { ...requestConfig.headers, Authorization: `Bearer ${token}` }
    }

    const effectiveUserId = getEffectiveUserId()
    if (effectiveUserId && shouldAttachUserId(endpoint)) {
      data = { ...data, userId: data.userId ?? effectiveUserId }
    }

    console.log('🔧 Request config:', {
      endpoint,
      timeout: requestConfig.timeout || 'default (10min)',
      maxContentLength: requestConfig.maxContentLength,
      maxBodyLength: requestConfig.maxBodyLength,
      hasData: !!data,
      dataKeys: data ? Object.keys(data) : []
    })

    const response = await api.post(endpoint, data, requestConfig)
    return response.data
  } catch (error) {
    console.error('POST API Error:', error)
    throw error
  }
}

export const putDataAPI = async (endpoint: string, data: any, token?: string) => {
  try {
    const config: any = {}
    if (token) {
      config.headers = { Authorization: `Bearer ${token}` }
    }

    const effectiveUserId = getEffectiveUserId()
    if (effectiveUserId && shouldAttachUserId(endpoint)) {
      data = { ...data, userId: data.userId ?? effectiveUserId }
    }

    const response = await api.put(endpoint, data, config)
    return response.data
  } catch (error) {
    console.error('PUT API Error:', error)
    throw error
  }
}

export const deleteDataAPI = async (
  endpoint: string,
  token?: string,
  extra?: { params?: Record<string, string> }
) => {
  try {
    const config: any = {}
    if (token) {
      config.headers = { Authorization: `Bearer ${token}` }
    }

    const effectiveUserId = getEffectiveUserId()
    const mergedParams: Record<string, string> = { ...(extra?.params || {}) }
    if (effectiveUserId && shouldAttachUserId(endpoint) && mergedParams.userId == null) {
      mergedParams.userId = effectiveUserId
    }
    if (Object.keys(mergedParams).length > 0) {
      config.params = mergedParams
    }

    const response = await api.delete(endpoint, config)
    return response.data
  } catch (error) {
    console.error('DELETE API Error:', error)
    throw error
  }
}

export const TEAM_CONTEXT_CACHE_PREFIXES = [
  'showsPageCache',
  'musicPageCatalogCache',
  'showsTicketTrackingOverrides',
  'pendingBandsintownShows',
  'teamMembersCache',
  'teamHubCache',
  'dizee:granularPerms',
]

/**
 * Clears active artist IDs, legacy selectedArtist, and all caches that are scoped
 * to the current "acting as" artist (team member context). Call when the user was
 * removed from a team so they no longer see that artist's data.
 */
export const clearStaleSelectedArtistAndCaches = () => {
  const keysToRemove: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && TEAM_CONTEXT_CACHE_PREFIXES.some((p) => key.startsWith(p))) {
      keysToRemove.push(key)
    }
  }
  keysToRemove.forEach((k) => localStorage.removeItem(k))
  localStorage.removeItem(SELECTED_ARTIST_KEY)
  localStorage.removeItem(ACTIVE_ARTIST_IDS_KEY)
  localStorage.removeItem(ARTIST_CACHE_KEY)
  sessionStorage.clear()
}

/**
 * Sets active artists to a single artist and clears page caches.
 * Used when migrating from "Enter" or when switching to only this artist.
 * Caller may navigate and/or reload after.
 */
export const enterArtistContext = (artist: { _id: string; name?: string; [key: string]: unknown }) => {
  const keysToRemove: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && TEAM_CONTEXT_CACHE_PREFIXES.some((p) => key.startsWith(p))) {
      keysToRemove.push(key)
    }
  }
  keysToRemove.forEach((k) => localStorage.removeItem(k))
  sessionStorage.clear()
  const id = artist._id || (artist as any).id
  if (id) setActiveArtistIds([id])
  localStorage.removeItem(SELECTED_ARTIST_KEY)
}

/**
 * POST request that reads an SSE (Server-Sent Events) stream.
 * Returns parsed events as they arrive via callbacks, and resolves
 * with the 'complete' event payload or rejects on 'error'.
 */
export async function postSSE<T = any>(
  endpoint: string,
  data: any,
  onProgress?: (payload: { percentage: number; message: string }) => void,
): Promise<T> {
  const baseURL = `${API_BASE_URL}/api/v1`
  const url = `${baseURL}/${endpoint}`

  let token = localStorage.getItem('dizeeToken')
  if (token) {
    try { token = JSON.parse(token) } catch { /* use as-is */ }
  }

  const effectiveUserId = getEffectiveUserId()
  if (effectiveUserId && shouldAttachUserId(endpoint)) {
    data = { ...data, userId: data.userId ?? effectiveUserId }
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(data),
  })

  if (!response.ok && !response.headers.get('content-type')?.includes('text/event-stream')) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`)
  }

  const reader = response.body!.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  return new Promise<T>((resolve, reject) => {
    /** Merged from `catalog_chunk` when the server streams large discovery payloads. */
    const catalogChunkTracks = new Map<number, unknown[]>()

    const pump = async () => {
      try {
        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          // Delimit SSE events on LF or CRLF; avoid broken JSON from trailing \r
          const parts = buffer.split(/\r?\n\r?\n/)
          buffer = parts.pop() || ''

          for (const part of parts) {
            if (!part.trim()) continue
            const lines = part.split(/\r?\n/)
            let eventType = ''
            const dataLineParts: string[] = []
            for (const line of lines) {
              const norm = line.replace(/\r$/, '')
              if (norm.startsWith(':')) continue // SSE comment (keepalive)
              if (norm.startsWith('event: ')) eventType = norm.slice(7).trim()
              if (norm.startsWith('data: ')) dataLineParts.push(norm.slice(6))
            }
            const eventData = dataLineParts.join('\n').trim()
            if (!eventData) continue

            try {
              const parsed = JSON.parse(eventData)
              if (eventType === 'progress') {
                onProgress?.(parsed)
              } else if (eventType === 'catalog_chunk') {
                const idx = parsed.chunkIndex
                const tr = parsed.tracks
                if (typeof idx === 'number' && Array.isArray(tr)) {
                  catalogChunkTracks.set(idx, tr)
                }
              } else if (eventType === 'complete') {
                let payload = parsed as T
                const p = parsed as { chunked?: boolean; data?: unknown[]; discovered_count?: number }
                if (p.chunked && catalogChunkTracks.size > 0) {
                  const merged = [...catalogChunkTracks.entries()]
                    .sort((a, b) => a[0] - b[0])
                    .flatMap(([, tracks]) => tracks)
                  payload = { ...parsed, data: merged } as T
                } else if (
                  p.chunked &&
                  catalogChunkTracks.size === 0 &&
                  (p.discovered_count ?? 0) > 0
                ) {
                  reject(
                    new Error(
                      'Catalog discovery closed without receiving track data (connection may have been cut). Try again; if it persists, confirm API and app are on the latest version.',
                    ),
                  )
                  return
                }
                resolve(payload)
                return
              } else if (eventType === 'error') {
                reject(new Error(parsed.message || 'Discovery failed'))
                return
              }
            } catch {
              console.warn('[postSSE] Failed to parse event:', eventData.slice(0, 200))
            }
          }
        }
        reject(new Error('SSE stream ended without a complete event'))
      } catch (err) {
        reject(err)
      }
    }
    pump()
  })
}

export { api, isTeamMemberContext, getEffectiveUserId }
